import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ExternalLink, Lock, History, Milestone, Map as MapIcon, Hourglass, Check, AlertCircle, Ban } from 'lucide-react';
import { useT } from '../../i18n';
import { api } from '../../api/client';
import { TaskFilters, useProjectFilter, usePersonFilter } from '../../components/TaskFilters';
import { FilterToggle } from '../../components/FilterSelect';
import { usePageFilters } from '../../context/PageFiltersContext';
import { projectDisplayCode } from '../../utils/identifiers';
import type { DateChange, KanbanBoard, Project } from '../../types';
import { timelineApi, type TimelineData, type TimelineTask } from './api';
import { ChangeLogPanel } from './ChangeLogPanel';
import { dayNum, localYmd, plannedDay, dueDay, dateOfDay, shortDay, fullDay, plannedLabel } from './dates';

// [Proposition: timeline-taches] Replaces "Planification des tâches": one
// project's tasks on a grid with one column per week (about six on a laptop
// screen, then it scrolls). Each task is a band in its status colour, from the
// day it was created to its due date, with its dates written inside: planned
// (map), due (hourglass), Done (check), blocked (red no-entry sign), unblocked (green no-entry sign). A planned or due date that was pushed
// back stays on the band in grey; hovering it tells the initial date, when it
// was changed and the new date. Each row also shows its milestone's planned
// end. Several projects can be shown one below the other, with the same
// project / person / sponsor / collaborator filters as Organisation tâches. An orange button
// marks a task changed since the PM last validated it and opens its change log.

const WEEK_W = 150;           // px per week column
const DAY_W = WEEK_W / 7;
const LABEL_W = 300;          // sticky task column
const WEEKS_AFTER_TODAY = 4;  // always shown after today
const MAX_WEEKS = 104;
const NO_MILESTONE = '__none__';

/** Monday on or before the given day (epoch day 0 was a Thursday). */
const mondayOf = (n: number) => n - ((n + 3) % 7);
function isoWeek(n: number): number {
  const thursday = mondayOf(n) + 3;
  return Math.floor((thursday - dayNum(`${dateOfDay(thursday).getUTCFullYear()}-01-01`)) / 7) + 1;
}

type StatusKind = 'todo' | 'progress' | 'blocked' | 'done';
const STATUS_KINDS: StatusKind[] = ['todo', 'progress', 'blocked', 'done'];
function statusKind(task: TimelineTask): StatusKind {
  if (task.is_done) return 'done';
  if (/block|bloqu/i.test(task.column_label)) return 'blocked';
  if (/progress|cours/i.test(task.column_label)) return 'progress';
  return 'todo';
}

/** Earlier values a date was moved away from (not the current one), each with the change that moved it. */
function previousDates(history: DateChange[], current: string | null, toDay: (v: string) => string) {
  const currentDay = current ? toDay(current) : null;
  const seen = new Set<string>();
  const out: { day: string; change: DateChange }[] = [];
  for (const h of history ?? []) {
    if (!h.old_date) continue;
    const day = toDay(h.old_date);
    if (day === currentDay || seen.has(day)) continue;
    seen.add(day);
    out.push({ day, change: h });
  }
  return out;
}

function postponements(history: DateChange[], toDay: (v: string) => string): number {
  return (history ?? []).filter(h => h.old_date && h.new_date && toDay(h.new_date) > toDay(h.old_date)).length;
}

// ── Dates inside a task's band ──
// Two lanes (top: planned, bottom: due and Done); a date moves to the other
// lane when it would overlap one already placed, and a lone lane is centred.
type Lane = 'top' | 'bottom';
interface PinSpec {
  key: string;
  kind: 'planned' | 'due' | 'done' | 'blocked' | 'unblocked';
  lane: Lane;               // preferred lane
  previous?: boolean;
  late?: boolean;
  align: 'start' | 'end';   // icon on the day / pin ending at the band's end
  anchor: number;           // px: the day's centre, or the band's right edge
  label: string;
  title: string;
}
interface Pin extends PinSpec { from: number; to: number }

const PIN_ICON: Record<PinSpec['kind'], (size: number) => ReactNode> = {
  planned: (s) => <MapIcon size={s} />,
  due: (s) => <Hourglass size={s} />,
  done: (s) => <Check size={s} strokeWidth={3} />,
  blocked: (s) => <Ban size={s} strokeWidth={2.5} />,
  unblocked: (s) => <Ban size={s} strokeWidth={2.5} />,  // same sign in green: no lock, kept for private items
};

function layoutPins(specs: PinSpec[]): Pin[] {
  const placed: Pin[] = [];
  const width = (label: string) => 30 + label.length * 6.6; // icon + padding + text, approx.
  for (const p of specs) {
    const w = width(p.label);
    const from = p.align === 'start' ? p.anchor - 10 : p.anchor - w; // icon centred on the day
    const to = from + w;
    const other: Lane = p.lane === 'top' ? 'bottom' : 'top';
    const free = (lane: Lane) => !placed.some(q => q.lane === lane && from < q.to + 4 && q.from < to + 4);
    placed.push({ ...p, lane: free(p.lane) || !free(other) ? p.lane : other, from, to });
  }
  return placed;
}

function TaskTrack({ task, start, today, milestoneEnd }: { task: TimelineTask; start: number; today: number; milestoneEnd: string | null }) {
  const { t } = useT();
  const left = (n: number) => (n - start) * DAY_W;          // start of a day
  const centre = (n: number) => (n - start + 0.5) * DAY_W;  // middle of a day
  const kind = statusKind(task);
  const created = dayNum(task.created_on);
  const due = task.due_date ? dayNum(task.due_date) : null;
  const doneDay = task.done_on ? dayNum(task.done_on) : null;
  // The creation date can't change, so nothing is drawn before it: a date set
  // earlier than the creation (old data) sits on the band's first day, still
  // labelled with its real date.
  const onBand = (n: number) => Math.max(n, created);

  const plannedOn = task.planned_at ? dayNum(plannedDay(task.planned_at)) : null;

  // The band runs from the day the task was created to its latest date: due,
  // planned or Done, so every current date sits on it. A task not Done yet is
  // still running, so a late one reaches today.
  const runsTo = task.is_done ? created : today;
  const band = due != null
    ? { left: left(created), right: left(Math.max(created, due, plannedOn ?? created, doneDay ?? created, runsTo) + 1) }
    : null;

  const movedTitle = (p: { day: string; change: DateChange }, toDay: (v: string) => string) =>
    t('tlt.mark.moved', {
      date: fullDay(p.day, t),
      when: fullDay(p.change.changed_at, t),
      who: p.change.changed_by || '—',
      next: p.change.new_date ? fullDay(toDay(p.change.new_date), t) : t('tlt.log.none'),
    });

  const specs: PinSpec[] = [];
  if (due != null && band) {
    const late = !task.is_done && due < today;
    specs.push({ key: 'due', kind: 'due', lane: 'bottom', late, align: 'end', anchor: left(onBand(due) + 1) - 6, label: shortDay(due, t),
      title: t(late ? 'tlt.mark.dueLate' : 'tlt.mark.due', { date: fullDay(task.due_date!, t) }) });
  }
  if (task.planned_at) {
    const n = dayNum(plannedDay(task.planned_at));
    specs.push({ key: 'planned', kind: 'planned', lane: 'top', align: 'start', anchor: centre(onBand(n)), label: shortDay(n, t),
      title: t('tlt.mark.planned', { date: plannedLabel(task.planned_at, t) }) });
  }
  if (task.done_on) {
    const n = dayNum(task.done_on);
    specs.push({ key: 'done', kind: 'done', lane: 'bottom', align: 'start', anchor: centre(onBand(n)), label: shortDay(n, t),
      title: t('tlt.mark.done', { date: fullDay(task.done_on!, t) }) });
  }
  // Each time it was blocked, and unblocked; the reason is only known for the current block.
  for (const [i, b] of (task.blocked_periods ?? []).entries()) {
    const from = dayNum(b.from);
    const date = fullDay(b.from, t);
    const reason = b.to === null && task.blocked_reason;
    specs.push({ key: `bl${i}`, kind: 'blocked', lane: 'top', align: 'start', anchor: centre(onBand(from)), label: shortDay(from, t),
      title: reason ? t('tlt.mark.blockedReason', { date, reason }) : t('tlt.mark.blocked', { date }) });
    if (b.to) {
      const to = dayNum(b.to);
      specs.push({ key: `ub${i}`, kind: 'unblocked', lane: 'top', align: 'start', anchor: centre(onBand(to)), label: shortDay(to, t),
        title: t('tlt.mark.unblocked', { date: fullDay(b.to, t), since: date, n: to - from }) });
    }
  }
  for (const p of previousDates(task.planned_at_history, task.planned_at, plannedDay)) {
    const n = dayNum(p.day);
    specs.push({ key: `pp${p.day}`, kind: 'planned', lane: 'top', previous: true, align: 'start', anchor: centre(onBand(n)), label: shortDay(n, t), title: movedTitle(p, plannedDay) });
  }
  for (const p of previousDates(task.due_date_history, task.due_date, dueDay)) {
    const n = dayNum(p.day);
    specs.push({ key: `pd${p.day}`, kind: 'due', lane: 'bottom', previous: true, align: 'start', anchor: centre(onBand(n)), label: shortDay(n, t), title: movedTitle(p, dueDay) });
  }
  const pins = layoutPins(specs);
  const oneLane = pins.every(p => p.lane === pins[0]?.lane);

  return (
    <div className="tlt-track">
      {milestoneEnd && (
        <span className="tlt-ms-line" style={{ left: left(dayNum(milestoneEnd) + 1) }}
          title={t('tlt.milestoneEnd', { date: fullDay(milestoneEnd, t) })} />
      )}
      {band ? (
        <div className={`tlt-band is-${kind}`} style={{ left: band.left, width: band.right - band.left }}
          title={t('tlt.mark.band', { status: task.column_label, date: fullDay(task.created_on, t) })} />
      ) : (
        <span className="tlt-missing" style={{ left: left(created) + 4 }} title={t('tlt.missingDatesHelp')}>
          <AlertCircle size={12} /> {t('tlt.missingDates')}
        </span>
      )}
      {pins.map(p => (
        <span
          key={p.key}
          className={`tlt-pin is-${p.kind}${p.previous ? ' is-previous' : ''}${p.late ? ' is-late' : ''}${p.align === 'end' ? ' is-end' : ''} lane-${oneLane ? 'mid' : p.lane}`}
          style={p.align === 'start' ? { left: p.from } : { right: `calc(100% - ${p.anchor}px)` }}
          title={p.title}
        >
          {p.align === 'end' && <span className="tlt-pin-date">{p.label}</span>}
          <span className="tlt-pin-icon">{PIN_ICON[p.kind](12)}</span>
          {p.align === 'start' && <span className="tlt-pin-date">{p.label}</span>}
        </span>
      ))}
    </div>
  );
}

function TaskLabel({ task, dimmed, onOpenLog }: { task: TimelineTask; dimmed: boolean; onOpenLog: () => void }) {
  const { t } = useT();
  const kind = statusKind(task);
  const moved = postponements(task.due_date_history, dueDay) + postponements(task.planned_at_history, plannedDay);
  return (
    <div className={`tlt-label${dimmed ? ' is-dimmed' : ''}`}>
      <div className="tlt-label-top">
        <button type="button" className="tlt-title" onClick={onOpenLog} title={task.title}>{task.title}</button>
        {task.is_private && <Lock size={11} className="tlt-muted" />}
        <button type="button" className="tlt-icon-btn" onClick={onOpenLog} title={t('tlt.openLog')}><History size={13} /></button>
        {task.can_open && (
          <a className="tlt-icon-btn" href={`#tasks?task=${task.id}`} title={t('vjt.task.open')}><ExternalLink size={12} /></a>
        )}
      </div>
      <div className="tlt-label-meta">
        <span className={`tlt-status is-${kind}`} title={task.blocked_reason || undefined}>{task.column_label}</span>
        <span className="tlt-who">
          {task.assignee_name || t('vjt.task.unassigned')}
          {!!task.collaborator_names?.length && <span title={task.collaborator_names.join(', ')}> +{task.collaborator_names.length}</span>}
        </span>
        {moved > 0 && <span className="tlt-moved" title={t('tlt.movedHelp')}>{t('tlt.movedCount', { n: moved })}</span>}
        {task.pending_changes > 0 && (
          <button type="button" className="tlt-changes" onClick={onOpenLog} title={t('tlt.changesHelp')}>
            {t(task.pending_changes === 1 ? 'tlt.changesOne' : 'tlt.changes', { n: task.pending_changes })}
          </button>
        )}
      </div>
    </div>
  );
}

export function TimelineTaches() {
  const { t, lang } = useT();
  // Same filters (and same selection) as Organisation tâches — see components/TaskFilters.
  const { taskFilterPersons } = usePageFilters();
  const projectFilter = useProjectFilter();
  const personFilter = usePersonFilter();
  const [projects, setProjects] = useState<Project[]>([]);           // projects this user can fully open
  const [board, setBoard] = useState<KanbanBoard | null>(null);      // Organisation tâches' projects and cards
  const [timelines, setTimelines] = useState<Map<number, TimelineData>>(new Map());
  const [loading, setLoading] = useState(false);
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [logTaskId, setLogTaskId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([api.getProjects() as Promise<Project[]>, api.getKanbanBoard() as Promise<KanbanBoard>])
      .then(([ps, b]) => {
        // Only the projects this user is part of: the others show their overview or card only.
        setProjects(ps.filter(p => (p.access_level ?? 'full') === 'full').sort((a, b2) => a.name.localeCompare(b2.name)));
        setBoard(b);
      })
      .catch((e: Error) => alert(e.message));
  }, []);

  // The projects shown: Organisation tâches' projects, through the filters; with a
  // person selected, only those where they have a task.
  const shownIds = useMemo(() => {
    if (!board) return [] as number[];
    const byId = new Map(projects.map(p => [p.id, p]));
    return projects
      .filter(p => board.projects.some(bp => bp.id === p.id))
      .filter(p => projectFilter(byId.get(p.id), p.id))
      .filter(p => taskFilterPersons.length === 0
        || board.tasks.some(bt => bt.project_id === p.id && personFilter([bt.assignee_name, ...(bt.collaborator_names ?? [])])))
      .map(p => p.id);
  }, [board, projects, projectFilter, personFilter, taskFilterPersons]);
  const shownKey = shownIds.join(',');

  // Load the timelines of the shown projects (a few at a time); `force` refreshes the loaded ones too.
  const load = useCallback(async (ids: number[], force: boolean) => {
    const todo = force ? ids : ids.filter(id => !timelines.has(id));
    if (todo.length === 0) return;
    setLoading(true);
    try {
      const got = new Map<number, TimelineData>();
      for (let i = 0; i < todo.length; i += 6) {
        const chunk = await Promise.all(todo.slice(i, i + 6).map(id => timelineApi.getProject(id).then(d => [id, d] as const).catch(() => null)));
        chunk.forEach(c => { if (c) got.set(c[0], c[1]); });
      }
      setTimelines(prev => { const next = new Map(prev); got.forEach((d, id) => next.set(id, d)); return next; });
    } finally {
      setLoading(false);
    }
  }, [timelines]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(shownIds, false); }, [shownKey]);
  const reload = useCallback(() => { load(shownIds, true); }, [load, shownIds]);

  const shown = useMemo(() => shownIds.map(id => timelines.get(id)).filter((d): d is TimelineData => !!d), [shownIds, timelines]);
  const today = dayNum(localYmd(new Date()));

  // Weeks: from the first task's creation (nothing is drawn before a task's
  // creation) to the last date shown, and always a few weeks past today.
  const weeks = useMemo(() => {
    let lo = today, hi = today + WEEKS_AFTER_TODAY * 7;
    const take = (d: string | null | undefined) => { if (d) hi = Math.max(hi, dayNum(d)); };
    for (const data of shown) {
      for (const task of data.tasks) {
        lo = Math.min(lo, dayNum(task.created_on));
        [task.created_on, task.due_date, task.done_on, task.planned_at && plannedDay(task.planned_at)].forEach(take);
        (task.due_date_history ?? []).forEach(h => take(h.old_date && dueDay(h.old_date)));
        (task.planned_at_history ?? []).forEach(h => take(h.old_date && plannedDay(h.old_date)));
      }
      data.milestones.forEach(m => take(m.estimated_end));
    }
    const first = mondayOf(lo);
    const count = Math.min(MAX_WEEKS, Math.floor((mondayOf(hi) - first) / 7) + 1);
    return Array.from({ length: count }, (_, i) => first + i * 7);
  }, [shown, today]);
  const start = weeks[0] ?? mondayOf(today);

  // Month band above the weeks (a week belongs to the month of its Thursday).
  const months = useMemo(() => {
    const out: { key: string; label: string; span: number }[] = [];
    const fmt = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
    for (const w of weeks) {
      const d = dateOfDay(w + 3);
      const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
      const last = out[out.length - 1];
      if (last?.key === key) last.span++;
      else out.push({ key, label: fmt.format(d), span: 1 });
    }
    return out;
  }, [weeks, lang]);

  // Per project: its tasks grouped by milestone, in the project's milestone order.
  // The person filter hides the other tasks (as on Organisation tâches).
  const sections = useMemo(() => shown.map(data => {
    const tasks = data.tasks
      .filter(x => !onlyChanged || x.pending_changes > 0)
      .filter(x => personFilter([x.assignee_name, ...(x.collaborator_names ?? [])]));
    const byMs = new Map<string, TimelineTask[]>();
    for (const task of tasks) {
      const key = task.milestone_id && data.milestones.some(m => m.id === task.milestone_id) ? task.milestone_id : NO_MILESTONE;
      if (!byMs.has(key)) byMs.set(key, []);
      byMs.get(key)!.push(task);
    }
    const groups = data.milestones
      .filter(m => byMs.has(m.id))
      .map(m => ({ id: m.id, name: m.name || t('tlt.unnamedMilestone'), end: m.estimated_end, tasks: byMs.get(m.id)! }));
    if (byMs.has(NO_MILESTONE)) groups.push({ id: NO_MILESTONE, name: '', end: null, tasks: byMs.get(NO_MILESTONE)! });
    return { project: data.project, groups };
  }).filter(sec => sec.groups.length > 0), [shown, onlyChanged, personFilter, t]);

  const allTasks = useMemo(() => shown.flatMap(d => d.tasks), [shown]);
  const pendingTotal = allTasks.filter(x => x.pending_changes > 0).length;
  const logTask = allTasks.find(x => x.id === logTaskId) ?? null;
  const todayX = LABEL_W + (today - start + 0.5) * DAY_W;
  const todayWeek = Math.floor((today - start) / 7);
  const boardPeople = useMemo(() => (board?.tasks ?? []).flatMap(bt => [bt.assignee_name ?? '', ...(bt.collaborator_names ?? [])]), [board]);

  const jumpToday = useCallback(() => {
    // Today about two-thirds across the visible weeks: mostly the recent past, some of what's next.
    const el = scrollRef.current;
    if (el) el.scrollLeft = Math.max(0, (today - start) * DAY_W - (el.clientWidth - LABEL_W) * 0.62);
  }, [today, start]);
  useEffect(() => { if (sections.length) jumpToday(); }, [sections.length > 0, jumpToday]);

  return (
    <div className="tl-page tlt-page">
      <div className="toolbar page-toolbar">
        <div className="toolbar-filters">
          <TaskFilters projects={projects} people={boardPeople}>
            <FilterToggle label={t('tlt.onlyChanged', { n: pendingTotal })} checked={onlyChanged} onChange={setOnlyChanged} />
          </TaskFilters>
        </div>
      </div>

      <div className="tlt-legend">
        {STATUS_KINDS.map(k => (
          <span key={k} className="tlt-legend-item"><span className={`tlt-swatch is-${k}`} />{t(`tlt.status.${k}`)}</span>
        ))}
        <span className="tlt-legend-sep" />
        <span className="tlt-legend-item"><span className="tlt-pin is-planned is-legend"><span className="tlt-pin-icon">{PIN_ICON.planned(12)}</span></span>{t('tlt.legend.planned')}</span>
        <span className="tlt-legend-item"><span className="tlt-pin is-due is-legend"><span className="tlt-pin-icon">{PIN_ICON.due(12)}</span></span>{t('tlt.legend.due')}</span>
        <span className="tlt-legend-item"><span className="tlt-pin is-done is-legend"><span className="tlt-pin-icon">{PIN_ICON.done(12)}</span></span>{t('tlt.legend.done')}</span>
        <span className="tlt-legend-item"><span className="tlt-pin is-blocked is-legend"><span className="tlt-pin-icon">{PIN_ICON.blocked(12)}</span></span>{t('tlt.legend.blocked')}</span>
        <span className="tlt-legend-item"><span className="tlt-pin is-unblocked is-legend"><span className="tlt-pin-icon">{PIN_ICON.unblocked(12)}</span></span>{t('tlt.legend.unblocked')}</span>
        <span className="tlt-legend-item"><span className="tlt-pin is-due is-previous is-legend"><span className="tlt-pin-icon">{PIN_ICON.due(12)}</span></span>{t('tlt.legend.previous')}</span>
        <span className="tlt-legend-item"><span className="tlt-legend-msline" />{t('tlt.legend.msEnd')}</span>
        <span className="tlt-legend-item"><span className="tlt-legend-today" />{t('planning.today')}</span>
        <button type="button" className="btn btn-ghost btn-sm tlt-legend-today-btn" onClick={jumpToday}>{t('planning.jumpToday')}</button>
      </div>

      {(!board || (loading && shown.length === 0)) ? <div className="spinner" /> : sections.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">{t('tlt.emptyTitle')}</div>
          <div className="empty-state-desc">{t('tlt.emptyDesc')}</div>
        </div>
      ) : (
        <div className="tlt-scroll" ref={scrollRef}>
          <div className="tlt-grid" style={{ '--tlt-cols': `${LABEL_W}px repeat(${weeks.length}, ${WEEK_W}px)`, '--tlt-week': `${WEEK_W}px` } as CSSProperties}>
            {/* The current week, tinted under every row. */}
            {todayWeek >= 0 && todayWeek < weeks.length && (
              <div className="tlt-today-col" style={{ left: LABEL_W + todayWeek * WEEK_W, width: WEEK_W }} />
            )}

            <div className="tlt-corner">{t('tlt.taskColumn')}</div>
            {months.map(m => (
              <div key={m.key} className="tlt-month" style={{ gridColumn: `span ${m.span}` }}>
                <span>{m.label}</span>
              </div>
            ))}
            {weeks.map((w, i) => (
              <div key={w} className={`tlt-week${i === todayWeek ? ' is-current' : ''}`} style={{ gridRow: 2, gridColumn: i + 2 }}>
                <span className="tlt-week-num">{t('tlt.weekShort', { n: isoWeek(w) })}</span>
                <span className="tlt-week-range">{shortDay(w, t)} – {shortDay(w + 6, t)}</span>
              </div>
            ))}

            {sections.map(sec => (
              <Fragment key={sec.project.id}>
                {/* Project header row (several projects can be shown one below the other). */}
                <a className="tlt-proj-label" href={`#project?id=${sec.project.id}`} title={t('tasks.backToProject')}>
                  <span className="tlt-proj-code">{(sec.project.project_code || '').toUpperCase()}</span>
                  <span className="tlt-proj-name">{sec.project.name}</span>
                </a>
                <div className="tlt-proj-track" />
                {sec.groups.map(g => (
                  <Fragment key={`${sec.project.id}:${g.id}`}>
                    <div className="tlt-ms-label">
                      <Milestone size={14} className="tlt-ms-icon" />
                      <span className="tlt-ms-kind">{g.id === NO_MILESTONE ? t('tlt.tasksWithout') : t('tlt.milestone')}</span>
                      <span className="tlt-ms-name" title={g.name}>{g.name}</span>
                    </div>
                    <div className="tlt-ms-track">
                      {g.end && (
                        <span className="tlt-ms-end" style={{ left: (dayNum(g.end) + 1 - start) * DAY_W }}>
                          <span className="tlt-ms-end-label">{t('tlt.milestoneEndShort', { date: shortDay(dayNum(g.end), t) })}</span>
                        </span>
                      )}
                    </div>
                    {g.tasks.map(task => (
                      <Fragment key={task.id}>
                        <TaskLabel task={task} dimmed={false} onOpenLog={() => setLogTaskId(task.id)} />
                        <div className="tlt-track-cell">
                          <TaskTrack task={task} start={start} today={today} milestoneEnd={g.end} />
                        </div>
                      </Fragment>
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}

            <div className="tlt-today-line" style={{ left: todayX }} title={t('planning.today')} />
          </div>
        </div>
      )}

      {logTask && (
        <ChangeLogPanel task={logTask} onClose={() => setLogTaskId(null)} onValidated={reload} />
      )}
    </div>
  );
}
